import { floorplanRooms } from "./floorplan";

/** Calendar strip: structured fields so day chips never clip awkwardly */
export const weekDays = [
  { label: "Mon", date: "Apr 28", month: "Apr", dayNum: "28", longDate: "Mon, Apr 28" },
  { label: "Tue", date: "Apr 29", month: "Apr", dayNum: "29", longDate: "Tue, Apr 29" },
  { label: "Wed", date: "Apr 30", month: "Apr", dayNum: "30", longDate: "Wed, Apr 30" },
  { label: "Thu", date: "May 1", month: "May", dayNum: "1", longDate: "Thu, May 1" },
  { label: "Fri", date: "May 2", month: "May", dayNum: "2", longDate: "Fri, May 2" },
  { label: "Sat", date: "May 3", month: "May", dayNum: "3", longDate: "Sat, May 3" },
  { label: "Sun", date: "May 4", month: "May", dayNum: "4", longDate: "Sun, May 4" },
];

function makeControls(roomId) {
  return {
    roomId,
    equipment: [],
    standalones: {
      damatexCage: {
        present: false,
        subSensors: { temp: false, humidity: false, co2: false },
        calibrationNeeded: false,
        lastCalDate: null,
      },
      lightIntrusion: { present: false },
      rotorFlow: { present: false },
    },
    outputs: [
      { id: "out_fert", type: "fertilizer", label: "Fertilizer Output" },
      { id: "out_water", type: "fresh_water", label: "Fresh Water Output" },
    ],
  };
}

/* ── default board scaffold for a room ── */

function makeDefaultBoard(roomId, label, zone) {
  // Grow rooms (flower, veg, dry, clone) get sensor/service/virtual slots
  // Processing & logistics rooms get simpler boards
  const isGrow = ["flower", "veg", "dry", "clone"].includes(zone);

  const components = isGrow
    ? [
        { id: "out_1", type: "output", name: `${label} Light Rail`, spotType: "output" },
        { id: "out_2", type: "output", name: `${label} Irrigation`, spotType: "output" },
        { id: "sen_1", type: "sensor", name: `${label} Canopy Temp`, spotType: "sensor" },
        { id: "sen_2", type: "sensor", name: `${label} Humidity`, spotType: "sensor" },
        {
          id: "svc_1",
          type: "service",
          name: `${label} Climate Svc`,
          spotType: "service",
          expectedSensorId: "sen_1",
        },
        {
          id: "virt_1",
          type: "virtual",
          name: `${label} VPD`,
          spotType: "virtual",
          formula: "temp + humidity",
        },
      ]
    : [
        { id: "out_1", type: "output", name: `${label} Output 1`, spotType: "output" },
        { id: "sen_1", type: "sensor", name: `${label} Sensor 1`, spotType: "sensor" },
      ];

  return {
    components,
    board: {
      output: [null, null, null, null],
      sensor: [null, null, null, null],
      service: [null, null, null, null],
      virtual: [null, null, null, null],
    },
    connections: [],
  };
}

/* ── status / event seeds per zone ── */

function zoneDefaults(zone) {
  switch (zone) {
    case "flower":
      return {
        stage: "In cycle",
        status: "steady",
        reason: "Active flower cycle — monitor environment closely.",
        notes: "",
        recentHarvest: false,
        weeklyEvents: [],
      };
    case "veg":
      return {
        stage: "In cycle",
        status: "steady",
        reason: "Veg room in active rotation.",
        notes: "",
        recentHarvest: false,
        weeklyEvents: [],
      };
    case "dry":
      return {
        stage: "In cycle",
        status: "steady",
        reason: "Dry room — temperature and humidity critical.",
        notes: "",
        recentHarvest: false,
        weeklyEvents: [],
      };
    case "clone":
      return {
        stage: "In cycle",
        status: "steady",
        reason: "Clone room — maintain propagation environment.",
        notes: "",
        recentHarvest: false,
        weeklyEvents: [],
      };
    default:
      return {
        stage: "Operational",
        status: "steady",
        reason: "",
        notes: "",
        recentHarvest: false,
        weeklyEvents: [],
      };
  }
}

/* ── build a template for every room on the floorplan ── */

export const roomTemplates = floorplanRooms.map((fp) => {
  const defaults = zoneDefaults(fp.zone);
  const boardData = makeDefaultBoard(fp.id, fp.label, fp.zone);

  return {
    id: fp.id,
    label: fp.label,
    zone: fp.zone,
    ...defaults,
    ...boardData,
    controls: makeControls(fp.id),
  };
});
